// ShipperConfigPage
// This page renders the Shipper configuration list as a standalone route,
// preserving per-tab isolation (for CRUD tabs) and consistent tab titles.
//
// It replaces the previous Tabs-in-page approach used in ShipperPage for the
// "config" section, keeping navigation simple and aligned with the new
// top-level menu routes.
//
// Routes that should land here (already configured in your router):
//   /shippers           -> renders ShipperConfigPage
//
// Notes:
// - We keep per-tab state for CRUD operations in M_State (keys "operation" and "ShipperLine").
// - MTable already handles its own per-tab/session behavior when needed.
// - We update the tab title using the i18n key that matches the MenuDisplay
//   value from the backend seed:
//      MenuShippersManagementLabel
// - We do not auto-navigate between routes here; we only render this section.

import PropTypes from 'prop-types';
import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import {
  LoadingContext,
  M_StateActions,
  MTable,
  NavigateContext,
} from 'src/M_Components';

export default function ShipperConfigPage({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const matches = useMediaQuery('(min-width:900px)');
  const M_State = useSelector((state) => state.StateKeyValue);
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  // ------------------------------------------------------------------------------------------------------------
  // EFFECT
  // Update tab title when language changes. This keeps the tab label contextual.
  // IMPORTANT: we use the same i18n key as the backend MenuDisplay value:
  //   MenuShippersManagementLabel
  // ------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTab) return; // If no tab selected, do nothing
    renameTab(String(selectedTab), t('MenuShippersManagementLabel'));
  }, [selectedTab, renameTab, t]);

  const [refresh, setRefresh] = useState(false);

  // Keep identity stable to avoid re-renders/refetches in children
  const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

  // Centralize CRUD route path
  const CRUD_ROUTE = '/home/shippers/shipperConfigCrud';

  //================================================================================================
  // openShipperCrud:
  // - Stable (useCallback) handler invoked by MTable context actions.
  // - Writes 'operation' (and 'ShipperLine' for non-create ops) into global state.
  // - Validates that a row exists for View/Edit; otherwise shows a localized error.
  // - Builds a null-safe tab label (falls back to empty string if Name is missing).
  // - Uses a centralized CRUD route constant for consistency.
  // - Keeping this callback stable prevents unnecessary re-renders and duplicate API calls.
  //================================================================================================
  const openShipperCrud = useCallback(
    (operation, row, label) => {
      // Update global state with operation type
      dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

      // For View/Edit, ensure a row/shipper data is selected
      if (operation !== 'Create') {
        if (!row) {
          toastMessages('error', '', t('SelectedLine')); // If no row, show error and abort
          return;
        }
        dispatch(M_StateActions.addM_State({ key: 'ShipperLine', value: row })); // Store selected shipper data
      }

      // Construct tab label, safely handling missing Name
      const tabLabel =
        operation === 'Create'
          ? label
          : `${label} "${row?.Name ?? ''}"`;

      // Invoke tab creation with centralized route and constructed label
      onCreateTab(CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, toastMessages, t]
  );

  //================================================================================================
  // replaceFunctions:
  // - Memoized Map delivered to MTable to resolve context actions.
  // - Its identity is stable thanks to useMemo, avoiding downstream re-renders.
  // - Each entry uses the stable openShipperCrud callback and localized labels.
  //=================================================================================================
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openShipperCrud('View', row, t('LabelViewShipper'))],
        ['Create', () => openShipperCrud('Create', null, t('LabelNewShipper'))],
        ['Edit', (row) => openShipperCrud('Edit', row, t('LabelEditShipper'))],
      ]),
    [openShipperCrud, t]
  );

  return (
    <div
      className="divGlobalSettings"
      style={{ padding: '20px', display: 'flex', flexDirection: 'column' }}
    >
      {/* Title area shows the section title based on i18n keys */}
      <div
        className="titleSettings"
        style={{
          color: ctx.secondaryColor,
          padding: '20px',
          marginBottom: '20px',
          width: '80%',
        }}
      >
        {t('MenuShippersLabel')}
      </div>

      {/* Keep layout responsive as before. Only the Shippers config content is rendered here. */}
      <div style={matches ? { display: 'flex' } : {}}>
        <div className="settingsPageMainDiv" style={{ width: '100%' }}>
          <div className="subTitleSettings">
            <div>
              <div style={{ color: ctx.secondaryColor }}>{t('Shippers')}</div>
              <div className="labelSubtitleSettings">{t('InfoShippers')}</div>
            </div>
          </div>

          <MTable // dynamic table for shippers
            filtersOnTable
            entityName="Shippers"
            roleName="Shippers" // Module for 'Shippers' role
            rolePermissions={roles?.map((vl) => vl.Value)} // Array of permission strings
            removeContextItems={removeContextItems}
            refresh={refresh}
            setRefresh={setRefresh}
            useSessionStorage={false}
            replaceFunctions={replaceFunctions} // Stable map of context action handlers
            tabsRailWidth={250}
          />
        </div>
      </div>
    </div>
  );
}

ShipperConfigPage.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};
